import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  Platform,
  Image,
  TouchableOpacity,
  Alert,
  ScrollView,
  Modal,
  Pressable,
  Animated,
  ActivityIndicator,
} from 'react-native';
import {
  v2CreateExchange,
  v2GetExchangeFeeRate,
  v2GetMinExchangeAmount,
  v2GetMyBalances,
  v2GetRates,
  v2GetTokens,
  v2GetXStock,
} from '../../../services/v2/api';
import type {
  V2Balance,
  V2CreateExchangeRequest,
  V2RateInfo,
  V2Request,
  V2Token,
} from '../../../types/v2';
import TabBar from '../../../components/TabBar';
import NumericKeypad from '../../../components/NumericKeypad';
import { clearsMinimum, formatMinimumHint, minimumInToken } from '../minimumAmountRule';
import AmountCaret from '../../../components/UIComponents/AmountCaret';
import SimpleHeader from '../../../components/SimpleHeader';
import TokenIcon from '../../../components/UIComponents/TokenIcon';
import { CARD_SURFACE, COLORS } from '../../../theme/themeTokens';
import { COLORS_WRONG } from '../../../theme/colorsWrong';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useAuth } from '../../../contexts/AuthContext';
import { usePIN } from '../../../contexts/PINContext';
import PINConfirmModal from '../../../components/UIComponents/PINConfirmModal';
import { useSwipeDownDismiss } from '../../../hooks/useSwipeDownDismiss';
import { WEB_SHEET, WEB_SHEET_BACKDROP, WEB_SHEET_LIST } from '../../../../web/sheet';
import { numberToAmountDisplay, formatTokenAmount } from '../../../utils/amountFormat';
import { formatUsdtAmount, isUsdPegged } from '../../../utils/usdtEquivalent';
import StockIcon from '../../../components/UIComponents/StockIcon';
import { useXStocks } from '../../../hooks/useXStocks';
import { displayTicker, isStockAccountBalance } from '../../../utils/xstocks';
import { buildSellOptions, defaultSellOption } from './sellOptions';
import { resolveExchangePreset, type ExchangePreset } from './exchangePreset';
import {
  subtractDecimal,
  buyFromSell,
  exchangeFees,
  feeChargedOnProceeds,
  feeSymbolFor,
  isCompatiblePair,
  pairKind,
  priceUnitSymbol,
  sellFromBuy,
  priceQuotedInverted,
  invertDecimal,
  rateToText,
} from './exchangeQuote';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';

/** Market takes the live rate; Limit waits for the price the client sets. */
type PriceMode = 'market' | 'limit';

/** Wording of the two explanation popups, from the design. */
const PRICE_HELP: Record<PriceMode, { title: string; body: string }> = {
  market: {
    title: 'By market price',
    body: 'The purchase will be made at the exchange rate applicable at the time of the Transaction. '
      + 'The rate shown here is for reference: it does not mean the purchase will be made at exactly that rate.',
  },
  limit: {
    title: 'Limit application',
    body: 'A limit order is placed on the exchange. The purchase is made once the price reaches '
      + 'exactly the price you set.',
  },
};

/** How often Currency rate is refreshed while the screen is open (3.3). */
const RATE_REFRESH_MS = 30_000;

/** Top inset used when the picker sheets are rendered inside Operations. */
const OPERATIONS_TOP_INSET = (Platform.OS === 'android' ? 28 : 20) + 161;

export type { ExchangePreset } from './exchangePreset';

interface ExchangeScreenProps {
  onBack: () => void;
  onNavigate: (screen: string) => void;
  /** When true, the parent (OperationsScreen) supplies header + tab bar. */
  embedded?: boolean;
  preset?: ExchangePreset | null;
}

/** Which amount the keypad is editing, if any. */
type KeypadTarget = 'sell' | 'buy' | 'price';

/**
 * "1 234,56" for the amount rows; empty stays empty.
 *
 * Half-typed values survive: the integer part is grouped, the fraction is
 * shown exactly as typed — including a lone separator at the end. Handing
 * "100." to numberToAmountDisplay returns "0" (it validates whole numbers),
 * which is how typing a comma used to wipe the amount, and "100.0" came back
 * as "100", so the next keystroke landed in the integer part.
 */
const toDisplay = (value: string, decimals: number): string => {
  if (value === '') { return ''; }
  const dot = value.indexOf('.');
  if (dot === -1) { return numberToAmountDisplay(value, 0); }
  const head = numberToAmountDisplay(value.slice(0, dot) || '0', 0);
  return `${head},${value.slice(dot + 1, dot + 1 + decimals)}`;
};

/** Back to the plain form the arithmetic runs on. */
const fromDisplay = (value: string): string =>
  value.replace(/\s/g, '').replace(',', '.');

/**
 * The circled question mark next to a price chip. Its own touch target, so
 * reading the explanation does not switch the mode.
 */
const HelpBadge: React.FC<{ active: boolean; onPress: () => void; label: string }> = ({
  active, onPress, label,
}) => (
  <TouchableOpacity
    onPress={onPress}
    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
    accessibilityRole="button"
    accessibilityLabel={label}
  >
    <View style={[styles.helpBadge, active ? styles.helpBadgeActive : null]}>
      <Text style={[styles.helpBadgeText, active ? styles.helpBadgeTextActive : null]}>?</Text>
    </View>
  </TouchableOpacity>
);

const ExchangeScreen: React.FC<ExchangeScreenProps> = ({
  onBack,
  onNavigate,
  embedded = false,
  preset = null,
}) => {
  const xStocks = useXStocks();
  const isXStock = (symbol: string) => xStocks.symbols.has(symbol);
  const ticker = (symbol: string) => displayTicker(symbol, xStocks.symbols);

  // ─── Sell side: the user's own balances ───────────────────────────────
  const [balances, setBalances] = useState<V2Balance[]>([]);
  const [selectedSell, setSelectedSell] = useState<V2Balance | null>(null);
  const [sellModalVisible, setSellModalVisible] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(true);
  const sellSwipe = useSwipeDownDismiss(sellModalVisible, () => setSellModalVisible(false));

  // ─── Buy side: the whole token catalogue ──────────────────────────────
  const [catalogTokens, setCatalogTokens] = useState<V2Token[]>([]);
  const [selectedBuy, setSelectedBuy] = useState<V2Token | null>(null);
  const [buyModalVisible, setBuyModalVisible] = useState(false);
  const buySwipe = useSwipeDownDismiss(buyModalVisible, () => setBuyModalVisible(false));

  // ─── Form state ───────────────────────────────────────────────────────
  const [sellInput, setSellInput] = useState('');
  const [buyInput, setBuyInput] = useState('');
  const [priceInput, setPriceInput] = useState('');
  /**
   * Which price the order uses (Figma "Exchange 9/10"). Market takes the
   * live rate, Limit the figure the client types. Market is the default.
   */
  const [priceMode, setPriceMode] = useState<PriceMode>('market');
  /** Which explanation popup is open, if any. */
  const [priceHelp, setPriceHelp] = useState<PriceMode | null>(null);

  const [feeRate, setFeeRate] = useState<number | null>(null);
  /** Fee as the server sent it ("0.005"), echoed back with the order. */
  const [feeRateRaw, setFeeRateRaw] = useState<string | null>(null);
  /** Live price of the stock in the pair, USDT per share (3.3). */
  const [stockPrice, setStockPrice] = useState<number | null>(null);
  const [minAmount, setMinAmount] = useState<number | null>(null);
  /** Currency of minAmount: the sell token for its own minimum, else USDT. */
  const [minCurrency, setMinCurrency] = useState<string>('USDT');
  const [rates, setRates] = useState<Record<string, V2RateInfo>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdOrder, setCreatedOrder] = useState<V2Request | null>(null);

  const sellSymbol = selectedSell?.token_symbol ?? '';
  const buySymbol = selectedBuy?.symbol ?? '';
  // What the user reads: plain tickers for xStocks ("TSLA", not "TSLAx").
  const sellLabel = ticker(sellSymbol);
  const buyLabel = ticker(buySymbol);

  /**
   * Token icon; xStocks use the admin logo or a letter, never a crypto
   * fallback. Nothing is drawn before a token is picked: the bundled icons
   * answer an unknown symbol with USDT's, so "Select token" sat next to a
   * Tether logo it had nothing to do with.
   */
  const renderTokenIcon = (symbol: string, iconUrl: string | null | undefined, style: object) => {
    if (!symbol) { return null; }
    return isXStock(symbol) ? (
      <View style={style}><StockIcon symbol={symbol} iconUrl={iconUrl} size={24} /></View>
    ) : (
      <TokenIcon symbol={symbol} iconUrl={iconUrl ?? null} style={style}/>
    );
  };

  /** Second line under a picked token: its network, or the name for xStocks. */
  const tokenSubtitle = (symbol: string, networkName: string | null | undefined, name: string | null | undefined) =>
    networkName || (isXStock(symbol) ? name ?? '' : '');
  const sellDecimals = selectedSell?.token_decimals ?? 2;
  const buyDecimals = selectedBuy?.decimals ?? 8;

  // ─── Loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingBalances(true);
        const res = await v2GetMyBalances();
        if (cancelled) return;
        setBalances(res.balances || []);
      } catch (err) {
        console.warn('[ExchangeScreen] balances failed:', err);
      } finally {
        if (!cancelled) setLoadingBalances(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await v2GetTokens();
        if (cancelled) return;
        setCatalogTokens((res.tokens || []).filter((t) => t.is_active));
      } catch (err) {
        console.warn('[ExchangeScreen] tokens failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fee and minimum are re-read whenever a token changes (3.5), so the order
  // is quoted with the settings in force when the user fills it in. The
  // minimum is the sell token's own row in the Amounts reference, falling
  // back to the general USDT minimum.
  const loadLimits = async (sellTokenId?: string) => {
    try {
      const res = await v2GetMinExchangeAmount(sellTokenId);
      const n = parseFloat(res.value);
      if (Number.isFinite(n) && n >= 0) {
        setMinAmount(n);
        setMinCurrency(res.currency ?? 'USDT');
      }
    } catch {
      // Silent — Minimum amount row stays hidden.
    }
  };

  useEffect(() => {
    loadLimits(selectedSell?.token_id);
  }, [selectedSell?.token_id]);

  // Market and limit orders are charged different rates, so the Fee row
  // follows the toggle. Quoting with the wrong one would have the server
  // refuse the order (exchange_quote_changed).
  useEffect(() => {
    let cancelled = false;
    v2GetExchangeFeeRate(priceMode)
      .then((res) => {
        const n = parseFloat(res.percent);
        if (cancelled || !Number.isFinite(n) || n < 0) { return; }
        setFeeRate(n);
        setFeeRateRaw(res.percent);
      })
      .catch(() => {
        // Silent — the Fee row stays hidden rather than showing a guess.
      });
    return () => { cancelled = true; };
  }, [priceMode]);

  const sellOptions = useMemo(
    () => buildSellOptions(balances, xStocks.symbols),
    [balances, xStocks.symbols],
  );

  // Only tokens that live on a network, plus xStocks. The catalogue also
  // holds virtual rows with no network — EUR for card balances, and 2-decimal
  // BTC/ETH/USDT used by internal accounts. They render as a bare "ETH" next
  // to the real "ETH-ETH" and are indistinguishable in the list, but buying
  // one is meaningless: it is an accounting entry, not a traded asset.
  // xStocks have no network either, yet they are what the user trades; they
  // follow the crypto tokens, as in the design.
  const tokens = useMemo(() => {
    const tradable = catalogTokens.filter(
      (t) => !!t.network_name || (xStocks.enabled && t.token_type === 'xstock'),
    );
    return tradable.sort((a, b) => {
      const sa = a.token_type === 'xstock' ? 1 : 0;
      const sb = b.token_type === 'xstock' ? 1 : 0;
      if (sa !== sb) return sa - sb;
      return a.symbol.localeCompare(b.symbol);
    });
  }, [catalogTokens, xStocks.enabled]);

  /** The user's available amount per token id, for the Buy list: current
   *  accounts, and xStocks accounts for shares. */
  const availableByTokenId = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const b of balances) {
      if ((b.account_type_code ?? 'current') !== 'current' && !isStockAccountBalance(b)) continue;
      totals[b.token_id] = (totals[b.token_id] ?? 0)
        + (parseFloat(b.available_amount ?? b.amount ?? '0') || 0);
    }
    return totals;
  }, [balances]);

  // Apply a preset once everything it refers to has loaded (effect below).
  const appliedPresetId = useRef<number | null>(null);

  // The biggest account by default, once balances are known — see
  // defaultSellOption. It used to be the first row of the list, which is
  // ordered by network, so the pick was whatever sat on TRON.
  useEffect(() => {
    const presetPicksSell = !!(preset?.sellTokenId || preset?.sellSymbol) && appliedPresetId.current !== preset?.id;
    if (!selectedSell && sellOptions.length > 0 && !presetPicksSell) {
      setSelectedSell(defaultSellOption(sellOptions)?.balance ?? null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellOptions]);

  useEffect(() => {
    if (!preset || appliedPresetId.current === preset.id) return;
    if (loadingBalances || xStocks.loading || catalogTokens.length === 0) return;
    appliedPresetId.current = preset.id;

    const resolved = resolveExchangePreset(
      preset, sellOptions, tokens, selectedSell, isXStock,
    );
    if (resolved.sell !== undefined) setSelectedSell(resolved.sell);
    if (resolved.buy) setSelectedBuy(resolved.buy);
    if (resolved.notHeld !== undefined) {
      Alert.alert('Nothing to sell', `You do not hold ${resolved.notHeld ? ticker(resolved.notHeld) : 'this token'} yet.`);
    }
    if (preset.price) setPriceInput(preset.price);
    setSellInput('');
    setBuyInput('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, loadingBalances, xStocks.loading, catalogTokens.length, sellOptions, tokens]);

  // The Buy list excludes the token row being sold (compared by id: an equal
  // ticker on another network is a different token) and, per 3.6, anything
  // that cannot face the Sell token: selling USDT buys anything, selling
  // anything else buys USDT, because the fee is charged in USDT.
  const buyChoices = useMemo(
    () => tokens
      .filter(
        (t) => t.id !== selectedSell?.token_id && isCompatiblePair(t.symbol, selectedSell?.token_symbol, isXStock),
      )
      // The list arrived in catalogue order, which for the stocks is no
      // order at all — a client selling USDT scanned a dozen and a half
      // tickers laid out as the catalogue happened to hold them. Stocks go
      // last and read alphabetically among themselves; crypto keeps the
      // order it came in. The Sell list already groups this way.
      .sort((a, b) => {
        const aStock = isXStock(a.symbol);
        const bStock = isXStock(b.symbol);
        if (aStock !== bStock) return aStock ? 1 : -1;
        if (!aStock) return 0;
        return ticker(a.symbol).localeCompare(ticker(b.symbol), undefined, { sensitivity: 'base' });
      }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokens, selectedSell?.token_id, selectedSell?.token_symbol, xStocks.symbols],
  );

  // Symmetrically: anything but USDT in Buy narrows Sell to USDT accounts,
  // and USDT in Buy leaves every other account standing.
  const sellChoices = useMemo(
    () => sellOptions.filter((o) => isCompatiblePair(o.symbol, selectedBuy?.symbol, isXStock)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [sellOptions, selectedBuy?.symbol, xStocks.symbols],
  );

  /**
   * 3.5: a token change clears the price and the derived amount.
   *
   * `keepSell` holds the sold amount, for when only the bought token
   * changed: the client typed how much they are parting with, and the
   * other side of the trade is no business of that figure. It used to go
   * with everything else, and the minimum landed in its place — a client
   * who had typed 0,4 AMZN and then picked a different token to receive
   * found their amount replaced.
   *
   * Changing the sold token clears it: the balance, the minimum and the
   * precision all moved, and the old number means nothing against them.
   */
  const resetForTokenChange = (keepSell = false) => {
    if (!keepSell) { setSellInput(''); }
    setBuyInput('');
    setPriceInput('');
    loadLimits(selectedSell?.token_id);
  };

  const pickSell = (balance: V2Balance) => {
    setSelectedSell(balance);
    if (selectedBuy && (selectedBuy.id === balance.token_id
      || !isCompatiblePair(selectedBuy.symbol, balance.token_symbol, isXStock))) {
      setSelectedBuy(null);
    }
    resetForTokenChange();
  };

  const pickBuy = (token: V2Token) => {
    setSelectedBuy(token);
    // Picking an incompatible pair moves the sold token too, and then the
    // amount goes with it.
    const sellChanged = !!selectedSell
      && !isCompatiblePair(selectedSell.token_symbol, token.symbol, isXStock);
    if (sellChanged) {
      const fallback = sellOptions.find((o) => isCompatiblePair(o.symbol, token.symbol, isXStock));
      setSelectedSell(fallback?.balance ?? null);
    }
    resetForTokenChange(!sellChanged);
  };

  // ─── Interdependent fields (3.5) ──────────────────────────────────────
  //
  // Price is the user's limit price, not the market rate. For crypto it is
  // 1 Buy token in Sell tokens (Sell = Buy × Price); for a stock it is always
  // 1 share in USDT, whichever side the stock is on. Rounding rules live in
  // exchangeQuote.ts.

  /** "0.010000" → "0.01"; a whole number keeps no dot at all. */
  const trimAmount = (value: string): string =>
    value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;

  /** Digits and one dot only, at most `decimals` fractional digits (3.1/3.2). */
  const sanitize = (raw: string, decimals: number): string => {
    const cleaned = raw.replace(',', '.').replace(/[^\d.]/g, '');
    const [head, ...rest] = cleaned.split('.');
    if (rest.length === 0) return head;
    return `${head}.${rest.join('').slice(0, decimals)}`;
  };

  const kind = pairKind(isXStock(sellSymbol), isXStock(buySymbol));
  const stockPair = kind !== 'crypto';
  // feeRate rides along: the fee is withheld from the sold amount before it
  // is converted, so every derived figure depends on it.
  // Selling a token for USDT the price reads "1 BTC = 84 000 USDT", the
  // other way round from the server's figure (see priceQuotedInverted).
  const priceInverted = priceQuotedInverted(kind, sellSymbol, buySymbol);
  const quoteCtx = { kind, sellDecimals, buyDecimals, sellSymbol, feeRate: feeRateRaw ?? undefined, priceInverted };

  /**
   * Market rate in the price's own terms: USDT per share for stock pairs
   * (the stock's USD price, taken as USDT 1:1), otherwise 1 Buy token in
   * Sell tokens; null when unknown.
   */
  const marketRate = useMemo<number | null>(() => {
    if (stockPair) return stockPrice;
    if (!sellSymbol || !buySymbol) return null;
    const buyInfo = rates[buySymbol.toUpperCase()] ?? rates[buySymbol];
    const sellInfo = rates[sellSymbol.toUpperCase()] ?? rates[sellSymbol];
    const buyUsd = parseFloat(buyInfo?.price_usd ?? '');
    const sellUsd = parseFloat(sellInfo?.price_usd ?? '');
    if (!Number.isFinite(buyUsd) || !Number.isFinite(sellUsd) || sellUsd <= 0) return null;
    return buyUsd / sellUsd;
  }, [rates, sellSymbol, buySymbol, stockPair, stockPrice]);

  /** The live rate as the form quotes it — inverted where the price is. */
  const displayRate = marketRate == null ? null : priceInverted ? 1 / marketRate : marketRate;

  /**
   * The price the order is built on: the live rate in Market mode, what the
   * client typed in Limit mode. Zero when it is not known yet — Sell and Buy
   * then do not derive each other, as before.
   */
  const priceValue = priceMode === 'market'
    ? displayRate ?? 0
    : parseFloat(priceInput) || 0;
  const sellValue = parseFloat(sellInput) || 0;
  const buyValue = parseFloat(buyInput) || 0;

  // The app's own keypad edits one amount at a time, like the other
  // Operations tabs; null means it is closed.
  const [keypadTarget, setKeypadTarget] = useState<KeypadTarget | null>(null);
  // The type the order went out with. Kept apart from the toggle so the
  // success sheet keeps saying what was actually sent.
  const [createdOrderType, setCreatedOrderType] = useState<PriceMode>('market');

  const handleSellChange = (raw: string) => {
    const next = sanitize(raw, sellDecimals);
    setSellInput(next);
    lastEditedRef.current = 'sell';
    const amount = parseFloat(next) || 0;
    // The price is only ever typed by the client; without it Sell and Buy
    // do not derive each other.
    if (priceValue > 0 && amount > 0) {
      setBuyInput(buyFromSell(amount, priceValue, quoteCtx));
    } else if (priceValue > 0 && amount === 0) {
      setBuyInput('');
    }
  };

  const handleBuyChange = (raw: string) => {
    const next = sanitize(raw, buyDecimals);
    setBuyInput(next);
    lastEditedRef.current = 'buy';
    const amount = parseFloat(next) || 0;
    if (priceValue > 0 && amount > 0) {
      setSellInput(sellFromBuy(amount, priceValue, quoteCtx));
    } else if (priceValue > 0 && amount === 0) {
      setSellInput('');
    }
  };

  // Only stores the price; repricing is the one effect below, so a typed
  // price and the live rate behave the same way.
  const handlePriceChange = (raw: string) => {
    setPriceInput(sanitize(raw, priceDecimals));
  };

  // The scale comes from the pair's own rate, not from what is being typed,
  // so the cap does not shift mid-entry. A price under 1 keeps its digits —
  // SHIB at 0.000023 USDT would otherwise read as 0,00 — and anything above
  // is money in USDT, so two.
  const priceDecimals = displayRate == null ? 8 : displayRate < 1 ? 8 : 2;

  /**
   * The live rate as a plain string. Full precision — it is what a market
   * order is created at, and rounding it to display precision would put the
   * order's own arithmetic out of step with the server's.
   */
  // In the server's direction and at its precision: toFixed(8) left
  // 0,0000119 BTC per USDT — three digits — and the order's amounts, worked
  // from the full rate, no longer matched its price.
  const marketPriceText = marketRate != null ? rateToText(marketRate) : '';

  /** The same rate at display precision — what the Limit field starts from. */
  const marketPriceRounded = displayRate != null
    ? String(Number(displayRate.toFixed(priceDecimals)))
    : '';

  /**
   * Switching the mode changes the price the form runs on, and the effect
   * below re-prices the amounts. Limit starts from the live rate so there is
   * something to edit.
   */
  const selectPriceMode = (mode: PriceMode) => {
    if (mode === priceMode) { return; }
    setPriceMode(mode);
    if (mode === 'limit' && !priceInput && marketPriceRounded) {
      setPriceInput(marketPriceRounded);
    }
  };

  /** What the keypad is showing, and how precise it may be. */
  // The keypad edits the value, it does not render it — the rows above do.
  // So it gets what was actually typed, with the separator it emits. Passing
  // the formatted string meant every keystroke travelled through a formatter
  // and back, and a value mid-typing does not survive that trip.
  const keypadValue = (keypadTarget === 'sell'
    ? sellInput
    : keypadTarget === 'buy'
      ? buyInput
      : priceInput).replace('.', ',');
  const keypadDecimals = keypadTarget === 'sell'
    ? sellDecimals
    : keypadTarget === 'buy'
      ? buyDecimals
      : priceDecimals;

  /** Keypad edits go through the same handlers as typing did. */
  const handleKeypadChange = (next: string) => {
    const plain = fromDisplay(next);
    if (keypadTarget === 'sell') { handleSellChange(plain); return; }
    if (keypadTarget === 'buy') { handleBuyChange(plain); return; }
    handlePriceChange(plain);
  };

  const handleKeypadClean = () => handleKeypadChange('');

  // The fee comes out of the amount, so the whole available balance can go
  // into an order.
  const handleKeypadMax = () => {
    const available = selectedSell?.available_amount ?? selectedSell?.amount ?? '0';
    handleSellChange(numberToAmountDisplay(available, sellDecimals).replace(/\s/g, '').replace(',', '.'));
  };

  /**
   * Repricing. Whenever the price the form runs on changes — the typed limit
   * price, the refreshed market rate, a switch between the two — or the fee
   * rate does, the side the client did not type follows it. A typed limit
   * price therefore wins over the live rate, and whichever amount the client
   * entered last stays as they left it.
   *
   * The amounts are read through refs: they are the input of this effect,
   * not its trigger, so re-deriving one does not run it again.
   */
  const lastEditedRef = useRef<'sell' | 'buy'>('sell');
  const sellInputRef = useRef(sellInput);
  const buyInputRef = useRef(buyInput);
  sellInputRef.current = sellInput;
  buyInputRef.current = buyInput;
  useEffect(() => {
    if (priceValue <= 0) { return; }
    const sell = parseFloat(sellInputRef.current) || 0;
    const buy = parseFloat(buyInputRef.current) || 0;
    if (lastEditedRef.current === 'buy' && buy > 0) {
      const next = sellFromBuy(buy, priceValue, quoteCtx);
      setSellInput((current) => (current === next ? current : next));
      return;
    }
    if (sell > 0) {
      const next = buyFromSell(sell, priceValue, quoteCtx);
      setBuyInput((current) => (current === next ? current : next));
      return;
    }
    // Only the other side is filled — derive from it whatever was typed last.
    if (buy > 0) {
      const next = sellFromBuy(buy, priceValue, quoteCtx);
      setSellInput((current) => (current === next ? current : next));
    }
    // quoteCtx is rebuilt on every render; the primitives it carries are the
    // real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceValue, kind, sellDecimals, buyDecimals, sellSymbol, feeRateRaw]);

  // ─── Derived figures ──────────────────────────────────────────────────

  const available = parseFloat(selectedSell?.available_amount ?? selectedSell?.amount ?? '0') || 0;
  const sellAmount = sellInput.replace(/\.$/, '');
  // Fee and Total Balance (3.4) — exact strings, sent with the order.
  const buyAmount = buyInput.replace(/\.$/, '');
  // Fee, Total Balance and proceeds (3.4). Selling a stock charges the fee in
  // USDT from the proceeds and locks only the shares.
  const quote = feeRateRaw != null && sellValue > 0
    ? exchangeFees({ kind, sell: sellAmount, buy: buyAmount, feeRate: feeRateRaw, sellSymbol, buySymbol, sellDecimals, buyDecimals })
    : null;
  // The fee's own token and precision, not the sold one's: selling BTC for
  // USDT the fee is USDT, and before Buy is picked the pair rule already
  // says so.
  const feeLabel = ticker(quote?.feeSymbol ?? feeSymbolFor(sellSymbol, buySymbol));
  const feeDecimals = feeChargedOnProceeds(sellSymbol) ? buyDecimals : sellDecimals;
  const totalDebit = quote ? parseFloat(quote.total) : sellValue;

  // Currency rate refreshes on a timer and never recalculates the form (3.5).
  useEffect(() => {
    const stockSymbol = kind === 'sellStock' ? sellSymbol : kind === 'buyStock' ? buySymbol : null;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await v2GetRates();
        if (!cancelled) setRates(res.rates ?? {});
      } catch {
        // Silent — Currency rate is a hint, not a gate.
      }
      if (stockSymbol) {
        try {
          const stock = await v2GetXStock(stockSymbol);
          const price = parseFloat(stock.price_usdt ?? '');
          if (!cancelled) setStockPrice(Number.isFinite(price) && price > 0 ? price : null);
        } catch {
          if (!cancelled) setStockPrice(null);
        }
      } else if (!cancelled) {
        setStockPrice(null);
      }
    };
    refresh();
    const timer = setInterval(refresh, RATE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [kind, sellSymbol, buySymbol]);

  /** Unit the price and rate are quoted in: USDT for stocks, else the Sell token. */
  const priceCurrencyLabel = stockPair ? 'USDT' : priceInverted ? buyLabel : sellLabel;
  /** "1 [symbol]" the price rows refer to (3.3). */
  const priceUnitLabel = priceInverted ? sellLabel : ticker(priceUnitSymbol(kind, sellSymbol, buySymbol));

  // A rate is a quotient, not a token amount, so the token's own precision is
  // the wrong yardstick: USDT is registered with 18 decimals in the catalogue
  // and 1 ETH came out as "2 471,7376196626083 USDT". Two digits above 1 and
  // eight below mirrors formatBalanceLikeCard in the Withdraw screen.
  const rateText = displayRate == null
    ? '—'
    : `${numberToAmountDisplay(displayRate, priceDecimals)} ${priceCurrencyLabel}`;

  // The threshold is a single global row (min_amount_exchange) denominated in
  // USDT, exactly like min_amount_withdraw. Comparing it against the raw sell
  // amount demanded "50 BTC" — four million dollars — so it is converted into
  // the sell token once, rounded UP to that token's precision, and then the
  // hint, the prefill and the check are all the same number. Cutting it
  // downward is what greyed the button out on the very figure the screen had
  // just put in the field. Unknown rate → fail open, same as the Withdraw
  // screen: never block when we cannot price.
  // A minimum in the sell token's own units (its row in the Amounts
  // reference) needs no conversion.
  const minInSellUnits = minCurrency === sellSymbol;
  const minAmountInSellToken: number | null = (() => {
    // A threshold already in the sell token's units, or a token worth a
    // dollar, needs no conversion — divide by one.
    if (minInSellUnits || isUsdPegged(sellSymbol)) {
      return minimumInToken(minAmount, 1, sellDecimals);
    }
    const info = rates[sellSymbol.toUpperCase()] ?? rates[sellSymbol];
    return minimumInToken(minAmount, parseFloat(info?.price_usd ?? ''), sellDecimals);
  })();

  // The Sell field opens on the minimum: it is the smallest order the server
  // accepts, and typing it by hand was the first thing every client did. Only
  // an empty field is filled, so nothing the client entered is overwritten;
  // a token change clears the field (resetForTokenChange) and the new token's
  // minimum lands here.
  useEffect(() => {
    if (minAmountInSellToken == null || minAmountInSellToken <= 0) { return; }
    // Nothing on the account: filling in the minimum would answer a client
    // who has just opened the screen with "Insufficient funds. Available: 0".
    if (available <= 0) { return; }
    if (sellInputRef.current !== '') { return; }
    setSellInput(trimAmount(minAmountInSellToken.toFixed(sellDecimals)));
    if (priceValue > 0) {
      setBuyInput(buyFromSell(minAmountInSellToken, priceValue, quoteCtx));
    }
    // quoteCtx is rebuilt every render; its primitives are in the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minAmountInSellToken, sellDecimals, priceValue, kind, buyDecimals, sellSymbol, feeRateRaw, available]);

  // Creating an order is hard-denied under impersonation (see
  // impersonation_policy.go), so the button is disabled rather than letting a
  // super-user fill the form and collect a 403 on submit. can_withdraw is the
  // nearest capability: both flows move the user's own funds.
  const caps = useCapabilities();
  const { isImpersonating } = useAuth();
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per order: a press after a lost answer resends it with the
  // same key, and the server does not file it twice.
  const idempotency = useIdempotencyKey<V2CreateExchangeRequest>();
  const [pinVisible, setPinVisible] = useState(false);

  // More than the account holds. Confirm is disabled for it either way, but
  // silently — and in Limit mode the sold amount is often not typed at all
  // but derived from the bought amount and the price, so it can drift past
  // the balance while the client is looking at the other field.
  //
  // Compared at the token's own precision: "Maximum" fills the field with
  // the whole available balance, and a float comparison would call that
  // over. The Top-up screen learned the same lesson in cents.
  // The fee on an exchange comes out of the amount, so everything the client
  // holds is spendable: a balance below the minimum can be emptied in one
  // order and the threshold steps aside. Otherwise a leftover 0,1 AMZN is
  // stuck for good — too little to trade, and stocks cannot be withdrawn.
  const meetsMinimum = clearsMinimum({
    amount: sellValue,
    minInToken: minAmountInSellToken,
    availableBalance: available,
    maxSpendable: available,
    decimals: sellDecimals,
  });
  const sellingDust =
    minAmountInSellToken != null && available > 0 && available < minAmountInSellToken;

  const sellScale = Math.pow(10, Math.min(Math.max(sellDecimals, 0), 8));
  const insufficientFunds =
    !!selectedSell &&
    sellValue > 0 &&
    Math.round(totalDebit * sellScale) > Math.round(available * sellScale);

  const canConfirm =
    !!selectedSell &&
    !!selectedBuy &&
    sellValue > 0 &&
    buyValue > 0 &&
    priceValue > 0 &&
    meetsMinimum &&
    totalDebit <= available &&
    // Selling a stock: the proceeds must cover the USDT fee.
    !(kind === 'sellStock' && quote != null && parseFloat(quote.receive) <= 0) &&
    !loadingBalances &&
    caps.can_withdraw &&
    !isImpersonating;

  // ─── Submit ───────────────────────────────────────────────────────────

  // Tapping Confirm asks for the PIN first, as Withdraw and the deposit
  // screens do — an exchange moves money and should not go through on a
  // single tap. Without a PIN set up (or with it skipped) the order is
  // created straight away, which is how the other screens behave too.
  const handleConfirm = () => {
    if (!canConfirm || isSubmitting || !selectedSell || !selectedBuy) { return; }
    if (!isPINSetup || isPINSkipped) {
      void submitOrder();
      return;
    }
    setPinVisible(true);
  };

  const submitOrder = async () => {
    setPinVisible(false);
    if (!canConfirm || isSubmitting || !selectedSell || !selectedBuy) return;
    setIsSubmitting(true);
    const request: V2CreateExchangeRequest = {
      balance_id: selectedSell.id,
      amount: sellAmount,
      destination_token_id: selectedBuy.id,
      destination_amount: buyInput.replace(/\.$/, ''),
      // The server's direction: a price typed the other way round goes out
      // as its exact inverse, at the 18 places the server keeps.
      price: priceMode === 'market'
        ? marketPriceText
        : priceInverted
          ? invertDecimal(priceInput.replace(/\.$/, ''))
          : priceInput.replace(/\.$/, ''),
      order_type: priceMode,
      // What the screen showed: the server stores the rate for reference and
      // refuses the order if fee or total no longer match its own figures.
      currency_rate: marketRate != null ? marketPriceText : undefined,
      fee_percent: feeRateRaw ?? undefined,
      fee_amount: quote?.fee,
      fee_currency: quote?.feeSymbol,
      total_amount: quote?.total,
    };
    try {
      // The rate, fee and total drift with the market; the order is the same
      // while what the client chose is.
      const attempt = idempotency.attempt(request, {
        balance_id: request.balance_id,
        amount: request.amount,
        destination_token_id: request.destination_token_id,
        order_type: request.order_type,
        price: request.order_type === 'limit' ? request.price : undefined,
      });
      const order = await v2CreateExchange(attempt.request, attempt.key);
      idempotency.answered();
      setCreatedOrderType(priceMode);
      setCreatedOrder(order);
    } catch (error) {
      idempotency.failed(error);
      const message = error instanceof Error
        ? error.message
        : 'An unexpected error occurred. Please try again.';
      Alert.alert('Exchange failed', message, [{ text: 'OK' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  // The created order's own figures. Its fee is in fee_currency; when that is
  // the bought token (a stock sale), only the amount was locked and the fee
  // comes out of the proceeds.
  const orderFeeSymbol = createdOrder?.fee_currency ?? createdOrder?.currency ?? sellSymbol;
  const orderFeeInBuyToken = !!createdOrder && orderFeeSymbol !== (createdOrder.currency ?? sellSymbol);
  // What actually left the balance. The fee sits inside the sold amount, so
  // that amount is the whole debit — the receipt used to add the fee on top
  // and announce "-102 USDT" for an order of 100. The order carries what it
  // locked (00131); older ones fall back to their amount, which under the
  // same rule is the same figure.
  const orderTotal = createdOrder
    ? createdOrder.locked_amount ?? createdOrder.amount ?? '0'
    : '0';
  const orderReceive = createdOrder
    ? orderFeeInBuyToken
      ? subtractDecimal(createdOrder.destination_amount ?? '0', createdOrder.fee ?? '0')
      : createdOrder.destination_amount ?? '0'
    : '0';

  const closeSuccess = () => {
    setCreatedOrder(null);
    onBack();
  };

  const formatOrderDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />
      {!embedded && (
        <SimpleHeader title="Exchange" onBack={onBack} backgroundVariant="primaryNavy" />
      )}

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* ── Sell ── */}
        <Text style={styles.sectionLabel}>Sell</Text>
        <View style={styles.fieldCard}>
          {loadingBalances ? (
            <View style={styles.selectorCenter}>
              <ActivityIndicator size="small" color={COLORS.brandOrange} />
            </View>
          ) : (
            <TouchableOpacity
              style={styles.selectorRow}
              onPress={() => setSellModalVisible(true)}
              activeOpacity={0.7}
            >
              {renderTokenIcon(sellSymbol, selectedSell?.icon_url, styles.selectorIcon)}
              <View style={styles.selectorTextContainer}>
                <Text style={styles.selectorTitle}>
                  {selectedSell
                    ? selectedSell.network_symbol
                      ? `${sellLabel} - ${selectedSell.network_symbol}`
                      : sellLabel
                    : 'Select account'}
                </Text>
                {selectedSell && tokenSubtitle(sellSymbol, selectedSell.network_name, selectedSell.token_name) ? (
                  <Text style={styles.selectorSubtitle}>
                    {tokenSubtitle(sellSymbol, selectedSell.network_name, selectedSell.token_name)}
                  </Text>
                ) : null}
              </View>
              <Image
                source={require('../../../assets/arrow_left_20.png')}
                style={styles.chevronIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.amountField}
            onPress={() => !isSubmitting && setKeypadTarget('sell')}
            disabled={isSubmitting}
            activeOpacity={0.7}
            testID="exchange-sell-amount"
          >
            <Text
              style={[
                styles.amountInput,
                !sellInput && styles.amountPlaceholder,
                insufficientFunds && styles.amountError,
              ]}
              numberOfLines={1}
            >
              {toDisplay(sellInput, sellDecimals) || '0'}
            </Text>
            <AmountCaret active={keypadTarget === 'sell'} />
            <Text
              style={[
                styles.amountSuffix,
                !sellInput && styles.amountPlaceholder,
                insufficientFunds && styles.amountError,
              ]}
            >
              {sellLabel}
            </Text>
          </TouchableOpacity>
        </View>
        {insufficientFunds ? (
          <Text style={styles.insufficientFundsText}>
            Insufficient funds on the current account. Available:{' '}
            {numberToAmountDisplay(available, sellDecimals)} {sellLabel}
          </Text>
        ) : null}

        {/* ── Buy ── */}
        <Text style={styles.sectionLabel}>Buy</Text>
        <View style={styles.fieldCard}>
          <TouchableOpacity
            style={styles.selectorRow}
            onPress={() => setBuyModalVisible(true)}
            activeOpacity={0.7}
          >
            {renderTokenIcon(buySymbol, selectedBuy?.icon_url, styles.selectorIcon)}
            <View style={styles.selectorTextContainer}>
              <Text style={styles.selectorTitle}>
                {selectedBuy
                  ? selectedBuy.network_symbol
                    ? `${buyLabel} - ${selectedBuy.network_symbol}`
                    : buyLabel
                  : 'Select token'}
              </Text>
              {selectedBuy && tokenSubtitle(buySymbol, selectedBuy.network_name, selectedBuy.name) ? (
                <Text style={styles.selectorSubtitle}>
                  {tokenSubtitle(buySymbol, selectedBuy.network_name, selectedBuy.name)}
                </Text>
              ) : null}
            </View>
            <Image
              source={require('../../../assets/arrow_left_20.png')}
              style={styles.chevronIcon}
              resizeMode="contain"
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.amountField}
            onPress={() => !isSubmitting && setKeypadTarget('buy')}
            disabled={isSubmitting}
            activeOpacity={0.7}
            testID="exchange-buy-amount"
          >
            <Text style={[styles.amountInput, !buyInput && styles.amountPlaceholder]} numberOfLines={1}>
              {toDisplay(buyInput, buyDecimals) || '0'}
            </Text>
            <AmountCaret active={keypadTarget === 'buy'} />
            <Text style={[styles.amountSuffix, !buyInput && styles.amountPlaceholder]}>{buyLabel}</Text>
          </TouchableOpacity>
        </View>

        {/* ── Market / Limit price ── */}
        <View style={[styles.priceModeCard, styles.infoCardSpacing]}>
          {/* The whole tile switches the mode, not just the chip inside it:
              it reads as one card, so the rate and the caption are part of
              the target. The help badge and the limit field are touchables
              of their own and win over this one. */}
          <TouchableOpacity
            style={[styles.priceTile, priceMode === 'market' ? styles.priceTileActive : null]}
            onPress={() => selectPriceMode('market')}
            disabled={marketRate == null || isSubmitting}
            activeOpacity={0.9}
            accessibilityRole="button"
            accessibilityState={{ selected: priceMode === 'market', disabled: marketRate == null }}
          >
            <View style={styles.priceTileHeader}>
              <View style={[styles.priceChip, priceMode === 'market' ? styles.priceChipActive : null]}>
                <Text style={[styles.priceChipText, priceMode === 'market' ? styles.priceChipTextActive : null]}>
                  Market price
                </Text>
                <HelpBadge
                  active={priceMode === 'market'}
                  onPress={() => setPriceHelp('market')}
                  label="About the market price"
                />
              </View>
            </View>
            <Text
              style={[styles.priceTileValue, priceMode === 'market' ? null : styles.priceTileValueMuted]}
              numberOfLines={1}
            >
              {`~ ${rateText}`}
            </Text>
            <Text style={styles.priceTileCaption}>
              Currency rate{priceUnitLabel ? `, 1 ${priceUnitLabel}` : ''}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.priceTile, priceMode === 'limit' ? styles.priceTileActive : null]}
            onPress={() => selectPriceMode('limit')}
            disabled={isSubmitting}
            activeOpacity={0.9}
            accessibilityRole="button"
            accessibilityState={{ selected: priceMode === 'limit' }}
          >
            <View style={styles.priceTileHeader}>
              <View style={[styles.priceChip, priceMode === 'limit' ? styles.priceChipActive : null]}>
                <Text style={[styles.priceChipText, priceMode === 'limit' ? styles.priceChipTextActive : null]}>
                  Limit price
                </Text>
                <HelpBadge
                  active={priceMode === 'limit'}
                  onPress={() => setPriceHelp('limit')}
                  label="About the limit price"
                />
              </View>
            </View>
            <View style={styles.priceTileInputRow}>
              <TouchableOpacity
                style={[styles.priceTileInput, styles.amountField]}
                onPress={() => {
                  if (isSubmitting) { return; }
                  selectPriceMode('limit');
                  setKeypadTarget('price');
                }}
                disabled={isSubmitting}
                activeOpacity={0.7}
                testID="exchange-price-input"
              >
                <Text
                  style={[
                    styles.priceTileValue,
                    styles.priceTileInputText,
                    priceMode === 'limit' ? null : styles.priceTileValueMuted,
                  ]}
                  numberOfLines={1}
                >
                  {toDisplay(priceInput, priceDecimals) || '0'}
                </Text>
                <AmountCaret active={keypadTarget === 'price'} />
              </TouchableOpacity>
              <Text style={[styles.priceTileUnit, priceMode === 'limit' ? null : styles.priceTileValueMuted]}>
                {priceCurrencyLabel}
              </Text>
            </View>
            <Text style={styles.priceTileCaption}>
              Set the target Price{priceUnitLabel ? `, 1 ${priceUnitLabel}` : ''}
            </Text>
          </TouchableOpacity>
        </View>

        {/* ── Minimum / Fee ── */}
        {/* The rule under the price tiles (Vector 660 in the design). */}
        <View style={styles.blockDivider} />

        <View style={[styles.summaryCard, styles.infoCardSpacing]}>
          {minAmount != null ? (
            <View style={styles.summaryRow}>
              <Text style={styles.infoLabel}>Minimum amount</Text>
              <Text style={styles.infoValue}>
                {minAmountInSellToken != null
                  ? `${formatMinimumHint(minAmountInSellToken, sellDecimals)} ${sellLabel}`
                  : formatUsdtAmount(minAmount, 0)}
              </Text>
            </View>
          ) : null}

          {minAmount != null && feeRate != null ? <View style={styles.summaryDivider} /> : null}

          {feeRate != null ? (
            <View style={styles.summaryRow}>
              <Text style={styles.infoLabel}>
                Fee, {(feeRate * 100).toFixed(2).replace(/\.?0+$/, '')}%
              </Text>
              <Text style={styles.infoValue}>
                {`${numberToAmountDisplay(quote?.fee ?? '0', feeDecimals)} ${feeLabel}`}
              </Text>
            </View>
          ) : null}

          {/* Why Confirm is dead. It used to just grey out, which left the
              client tapping a button that never said what was wrong. */}
          {sellValue > 0 && !meetsMinimum ? (
            <>
              <View style={styles.summaryDivider} />
              <Text style={styles.minimumNote}>
                {sellingDust
                  ? `Below the minimum. Your balance is under it too — sell all ${formatMinimumHint(available, sellDecimals)} ${sellLabel} to close the position.`
                  : 'Below the minimum for an exchange.'}
              </Text>
            </>
          ) : null}

          {/* Selling a stock pays the fee out of the proceeds, so what lands on
              the account is worth spelling out. The design has no such pair. */}
          {kind === 'sellStock' ? (
            <>
              <View style={styles.summaryDivider} />
              <View style={styles.summaryRow}>
                <Text style={styles.infoLabel}>You receive</Text>
                <Text style={styles.infoValue}>
                  {`${numberToAmountDisplay(quote && parseFloat(quote.receive) > 0 ? quote.receive : '0', buyDecimals)} ${buyLabel}`}
                </Text>
              </View>
            </>
          ) : null}
        </View>

        <TouchableOpacity
          style={[styles.confirmButton, (!canConfirm || isSubmitting) && styles.confirmButtonDisabled]}
          onPress={handleConfirm}
          activeOpacity={canConfirm && !isSubmitting ? 0.8 : 1}
          disabled={!canConfirm || isSubmitting}
        >
          <Text
            style={[
              styles.confirmButtonText,
              (!canConfirm || isSubmitting) && styles.confirmButtonTextDisabled,
            ]}
          >
            {isSubmitting ? 'Processing...' : 'Confirm transfer'}
          </Text>
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* ── Sell picker ── */}
      <Modal
        visible={sellModalVisible}
        transparent
        animationType="slide"
        statusBarTranslucent
        onRequestClose={() => setSellModalVisible(false)}
      >
        <View style={[styles.modalOverlay, WEB_SHEET_BACKDROP]}>
          {/* The backdrop is a sibling of the sheet, not its parent: a
              Pressable above the list takes the touch on Android and the
              native scroll never sees it. */}
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setSellModalVisible(false)} />
          <Animated.View
            style={[
              styles.modalContent,
              embedded && { marginTop: OPERATIONS_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: sellSwipe.translateY }] },
            ]}
            {...sellSwipe.panHandlers}
          >
            <View style={styles.modalHandle} />
            <View style={styles.modalTitleRow}>
              <Text style={styles.modalTitle}>Current accounts</Text>
              <Text style={styles.modalTitleAside}>Available balance</Text>
            </View>
            <ScrollView style={[styles.modalList, WEB_SHEET_LIST]} {...sellSwipe.listProps}>
                {sellChoices.length === 0 ? (
                  <Text style={styles.modalEmpty}>No current-account balances available.</Text>
                ) : null}
                {sellChoices.map((opt) => {
                  const isSelected = selectedSell?.id === opt.balance.id;
                  const optLabel = ticker(opt.symbol);
                  const title = opt.networkSymbol ? `${optLabel} - ${opt.networkSymbol}` : optLabel;
                  const subtitle = tokenSubtitle(opt.symbol, opt.networkName, opt.balance.token_name);
                  return (
                    <TouchableOpacity
                      key={opt.key}
                      style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                      onPress={() => {
                        pickSell(opt.balance);
                        setSellModalVisible(false);
                      }}
                      activeOpacity={0.7}
                    >
                      {renderTokenIcon(opt.symbol, opt.iconUrl, styles.modalOptionIcon)}
                      <View style={styles.modalOptionTextContainer}>
                        <Text style={styles.modalOptionName}>{title}</Text>
                        {subtitle ? (
                          <Text style={styles.modalOptionSubtitle}>{subtitle}</Text>
                        ) : null}
                      </View>
                      <Text style={styles.modalOptionAmount}>
                        {formatTokenAmount(opt.available, opt.balance.token_decimals)} {optLabel}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      {/* ── Buy picker ── */}
      <Modal
        visible={buyModalVisible}
        transparent
        animationType="slide"
        statusBarTranslucent
        onRequestClose={() => setBuyModalVisible(false)}
      >
        <View style={[styles.modalOverlay, WEB_SHEET_BACKDROP]}>
          {/* The backdrop is a sibling of the sheet, not its parent: a
              Pressable above the list takes the touch on Android and the
              native scroll never sees it. */}
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setBuyModalVisible(false)} />
          <Animated.View
            style={[
              styles.modalContent,
              embedded && { marginTop: OPERATIONS_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: buySwipe.translateY }] },
            ]}
            {...buySwipe.panHandlers}
          >
            <View style={styles.modalHandle} />
            <View style={styles.modalTitleRow}>
              <Text style={styles.modalTitle}>Buy</Text>
              <Text style={styles.modalTitleAside}>Available balance</Text>
            </View>
            <ScrollView style={[styles.modalList, WEB_SHEET_LIST]} {...buySwipe.listProps}>
                {buyChoices.length === 0 ? (
                  <Text style={styles.modalEmpty}>No tokens available.</Text>
                ) : null}
                {buyChoices.map((token) => {
                  const isSelected = selectedBuy?.id === token.id;
                  const tokenLabel = ticker(token.symbol);
                  const title = token.network_symbol
                    ? `${tokenLabel} - ${token.network_symbol}`
                    : tokenLabel;
                  const subtitle = tokenSubtitle(token.symbol, token.network_name, token.name);
                  return (
                    <TouchableOpacity
                      key={token.id}
                      style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                      onPress={() => {
                        pickBuy(token);
                        setBuyModalVisible(false);
                      }}
                      activeOpacity={0.7}
                    >
                      {renderTokenIcon(token.symbol, token.icon_url, styles.modalOptionIcon)}
                      <View style={styles.modalOptionTextContainer}>
                        <Text style={styles.modalOptionName}>{title}</Text>
                        {subtitle ? (
                          <Text style={styles.modalOptionSubtitle}>{subtitle}</Text>
                        ) : null}
                      </View>
                      <Text style={styles.modalOptionAmount}>
                        {numberToAmountDisplay(availableByTokenId[token.id] ?? 0, token.decimals)} {tokenLabel}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      {/* ── Request sent ── */}
      {/* The app's own keypad, as on Withdraw and Top-up: one amount at a
          time, with the available balance and Max / Clean for Sell. */}
      <NumericKeypad
        visible={keypadTarget !== null}
        value={keypadValue}
        onChange={handleKeypadChange}
        onClose={() => setKeypadTarget(null)}
        maxDecimals={keypadDecimals}
        availableValue={keypadTarget === 'sell'
          ? `${numberToAmountDisplay(selectedSell?.available_amount ?? selectedSell?.amount ?? '0', sellDecimals)} ${sellLabel}`
          : undefined}
        onMax={keypadTarget === 'sell' ? handleKeypadMax : undefined}
        onClean={handleKeypadClean}
        transparentOverlay
      />

      {/* Explanation of the chosen price type (Figma "Exchange 3"). */}
      <Modal
        visible={priceHelp != null}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setPriceHelp(null)}
      >
        <Pressable style={styles.helpOverlay} onPress={() => setPriceHelp(null)}>
          <Pressable style={styles.helpCard} onPress={() => {}}>
            <Text style={styles.helpTitle}>{priceHelp ? PRICE_HELP[priceHelp].title : ''}</Text>
            <Text style={styles.helpBody}>{priceHelp ? PRICE_HELP[priceHelp].body : ''}</Text>
            <TouchableOpacity
              style={styles.helpButton}
              onPress={() => setPriceHelp(null)}
              activeOpacity={0.85}
              accessibilityRole="button"
            >
              <Text style={styles.helpButtonText}>Ok</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={createdOrder != null}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={closeSuccess}
      >
        <View style={styles.successOverlay}>
          <View style={styles.successCard}>
            <TouchableOpacity style={styles.successClose} onPress={closeSuccess} activeOpacity={0.7}>
              <Text style={styles.successCloseText}>✕</Text>
            </TouchableOpacity>
            <Image
              source={require('../../../assets/wallet2.png')}
              style={styles.successIcon}
              resizeMode="contain"
            />
            <Text style={styles.successTitle}>Request sent</Text>
            <Text style={styles.successSubtitle}>Operation is being processed</Text>
            <View style={styles.successAmountRow}>
              <Text style={styles.successAmount}>
                -{numberToAmountDisplay(orderTotal, sellDecimals)}
              </Text>
              <Text style={styles.successCurrency}> {ticker(createdOrder?.currency ?? sellSymbol)}</Text>
            </View>

            <View style={styles.successDetails}>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Order type</Text>
                <Text style={styles.successDetailValue}>
                  {createdOrderType === 'market' ? 'Market' : 'Limit'}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Pair</Text>
                <Text style={styles.successDetailValue}>
                  {sellLabel} → {buyLabel}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Sell</Text>
                <Text style={styles.successDetailValue}>
                  {numberToAmountDisplay(createdOrder?.amount ?? '0', sellDecimals)} {sellLabel}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Buy</Text>
                <Text style={styles.successDetailValue}>
                  {numberToAmountDisplay(createdOrder?.destination_amount ?? '0', buyDecimals)} {buyLabel}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>At the rate</Text>
                <Text style={styles.successDetailValue}>
                  {numberToAmountDisplay(
                    priceInverted
                      ? (createdOrder?.conversion_rate ? invertDecimal(createdOrder.conversion_rate, 8) : priceInput)
                      : (createdOrder?.conversion_rate ?? priceInput),
                    priceDecimals,
                  )} {priceCurrencyLabel}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Fee</Text>
                <Text style={styles.successDetailValue}>
                  {numberToAmountDisplay(createdOrder?.fee ?? '0', orderFeeInBuyToken ? buyDecimals : sellDecimals)} {ticker(orderFeeSymbol)}
                </Text>
              </View>
              {/* No Total row: with the fee inside the amount it would only
                  repeat Sell. The fee line above says how much of that
                  amount went to it. */}
              {orderFeeInBuyToken ? (
                <View style={styles.successDetailRow}>
                  <Text style={styles.successDetailLabel}>You receive</Text>
                  <Text style={styles.successDetailValue}>
                    {numberToAmountDisplay(orderReceive, buyDecimals)} {buyLabel}
                  </Text>
                </View>
              ) : null}
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Date</Text>
                <Text style={styles.successDetailValue}>
                  {createdOrder ? formatOrderDate(createdOrder.created_at) : ''}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Request ID</Text>
                <Text style={styles.successDetailValue}>{createdOrder?.number ?? ''}</Text>
              </View>
            </View>

            <TouchableOpacity style={styles.successButton} onPress={closeSuccess} activeOpacity={0.8}>
              <Text style={styles.successButtonText}>Ok</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.successSecondaryButton}
              onPress={() => {
                setCreatedOrder(null);
                onNavigate('orders');
              }}
              activeOpacity={0.8}
            >
              <Text style={styles.successSecondaryText}>View request status</Text>
              <Image
                source={require('../../../assets/upper-right-arrow.png')}
                style={styles.successSecondaryIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <PINConfirmModal
        visible={pinVisible}
        title="Confirm exchange"
        subtitle={`${toDisplay(sellInput, sellDecimals) || '0'} ${sellLabel} → ${toDisplay(buyInput, buyDecimals) || '0'} ${buyLabel}`}
        onSuccess={submitOrder}
        onCancel={() => setPinVisible(false)}
      />

      {!embedded && <TabBar activeTab="dashboard" onNavigate={onNavigate} />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.primaryNavy },
  content: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  // 16 / 16 to the first label — the same opening gap as Inflow, Withdraw
  // and Top-up, so switching tabs does not shift the form.
  contentContainer: { paddingTop: 16, paddingHorizontal: 16, paddingBottom: 20 },
  sectionLabel: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
    marginTop: 16,
    marginBottom: 8,
  },
  // The same tile as the account picker on Withdraw (networkSelector there):
  // card surface with a shadow, 66 high, 28-px icon, the same type for the
  // name and the network line. Exchange was the odd one out (outlined,
  // smaller icon and text) among the four Operations tabs.
  fieldCard: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    ...CARD_SURFACE,
  },
  selectorRow: { flexDirection: 'row', alignItems: 'center', flexShrink: 1 },
  selectorCenter: { flex: 1, alignItems: 'flex-start', justifyContent: 'center', height: 40 },
  selectorIcon: { width: 28, height: 28, marginRight: 12 },
  selectorTextContainer: { justifyContent: 'center' },
  selectorTitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  selectorSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  chevronIcon: {
    width: 20,
    height: 20,
    transform: [{ rotate: '-90deg' }],
    marginLeft: 8,
  },
  // Value and ticker sit together against the right edge, as in the design.
  amountInput: {
    flexShrink: 1,
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.textPrimary,
    paddingVertical: 0,
    marginLeft: 8,
    textAlign: 'right',
  },
  amountSuffix: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.textPrimary,
  },
  infoCardSpacing: { marginTop: 20 },
  // Minimum / Fee: the same tile Withdraw, Top-up and the deposit screens
  // use — Pure Light under the card's own shadow, no border — so the four
  // forms read as one. Row height carries the spacing instead of a gap,
  // which is what lets the divider run the full width of the card.
  summaryCard: {
    ...CARD_SURFACE,
    backgroundColor: COLORS.pureLight,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  summaryDivider: { height: 1, backgroundColor: COLORS.borderMuted },
  // The rule between the price tiles and the summary (Vector 660).
  blockDivider: { height: 1, backgroundColor: COLORS.softGrey, marginTop: 20 },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 38,
  },
  // 16/20 — same as amountLabel / amountInput on Withdraw and Top-up.
  infoLabel: { fontFamily: 'Inter', fontSize: 16, lineHeight: 20, fontWeight: '400', color: COLORS.blueSteel },
  infoValue: { fontFamily: 'Inter', fontSize: 16, lineHeight: 20, fontWeight: '400', color: COLORS.blueSteel },
  priceInput: { flex: 1, marginLeft: 12, paddingVertical: 0 },
  // Market / Limit tiles. The selected one is a white card on the grey
  // strip, the other stays flat and muted.
  amountError: { color: COLORS.brandOrange },
  insufficientFundsText: {
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16,
    color: COLORS.brandOrange,
    marginTop: 6,
  },
  minimumNote: {
    fontFamily: 'Inter',
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 18,
    color: COLORS.brandOrange,
    paddingVertical: 10,
  },
  priceModeCard: {
    flexDirection: 'row',
    backgroundColor: COLORS.softGrey,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.softGrey,
    padding: 1,
  },
  // The chosen side turns into a white card inside the grey strip.
  priceTile: { flex: 1, borderRadius: 8, padding: 10 },
  priceTileActive: { backgroundColor: COLORS.white },
  priceTileHeader: { flexDirection: 'row' },
  priceChip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    alignSelf: 'stretch',
    flex: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: COLORS.primaryNavy,
    padding: 10,
  },
  priceChipActive: { backgroundColor: COLORS.primaryNavy, borderColor: COLORS.primaryNavy },
  priceChipText: { fontFamily: 'Inter', fontSize: 14, lineHeight: 16, fontWeight: '500', color: COLORS.primaryNavy },
  priceChipTextActive: { color: COLORS.white },
  helpBadge: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.primaryNavy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpBadgeActive: { borderColor: COLORS.white },
  helpBadgeText: { fontFamily: 'Inter', fontSize: 11, lineHeight: 13, color: COLORS.primaryNavy },
  helpBadgeTextActive: { color: COLORS.white },
  priceTileValue: {
    fontFamily: 'Inter',
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '600',
    color: COLORS.primaryNavy,
    marginTop: 10,
  },
  priceTileValueMuted: { color: COLORS.blueSteel },
  // The same gap the market tile leaves between its chip and its value, so
  // the two sides start at the same place.
  priceTileInputRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 6, marginTop: 10 },
  // Amounts are plain text; the app's keypad edits them.
  amountField: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' },
  amountPlaceholder: { color: COLORS.blueSteel, fontWeight: '400' },
  // The typed price sits on a rule, as in the design.
  priceTileInput: {
    flex: 1,
    paddingVertical: 0,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.blueSteel,
  },
  priceTileInputText: { flexShrink: 1, marginTop: 0, textAlign: 'left' },
  priceTileUnit: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '400',
    color: COLORS.primaryNavy,
  },
  priceTileCaption: {
    fontFamily: 'Inter',
    fontSize: 12,
    lineHeight: 16,
    color: COLORS.blueSteel,
    // Pinned to the bottom rather than spaced from what is above it. The
    // tiles sit in a row and stretch to a common height, so this lands both
    // captions on one line whatever each tile holds — a rate of one height,
    // an input field of another. Matching paddings drifted apart the moment
    // either side changed.
    marginTop: 'auto',
    paddingTop: 10,
  },
  // Explanation popup.
  helpOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  helpCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  helpTitle: {
    fontFamily: 'Inter',
    fontSize: 20,
    lineHeight: 26,
    fontWeight: '600',
    color: COLORS.primaryNavy,
    textAlign: 'center',
  },
  helpBody: {
    fontFamily: 'Inter',
    fontSize: 15,
    lineHeight: 21,
    color: COLORS.primaryNavy,
    textAlign: 'center',
    marginTop: 12,
  },
  helpButton: {
    height: 48,
    borderRadius: 10,
    backgroundColor: COLORS.primaryNavy,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
  },
  helpButtonText: { fontFamily: 'Inter', fontSize: 16, lineHeight: 20, fontWeight: '500', color: COLORS.white },
  // Shape and disabled colour of transferButton on the other Operations tabs.
  confirmButton: {
    marginTop: 20,
    height: 48,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.primaryNavy,
  },
  confirmButtonDisabled: { backgroundColor: COLORS_WRONG.disabled },
  confirmButtonText: { fontFamily: 'Inter', fontSize: 16, lineHeight: 20, fontWeight: '500', color: COLORS.white },
  confirmButtonTextDisabled: { color: COLORS.white },
  /* Bottom sheets */
  // Transparent, like the other Operations pickers: a dim layer above the
  // sheet reads as a grey band between the tabs and the sheet's top edge.
  // Absolutely positioned, not flex: 1 — on Android the Modal's own
  // container does not hand a definite height down, so a flexed overlay
  // sizes itself to its content: the sheet's ScrollView grew as tall as
  // the list, swallowed the sheet's bottom padding, ran off the screen
  // and had nothing left to scroll. Pinning all four edges gives the
  // chain a real height and the list scrolls again.
  modalOverlay: { ...StyleSheet.absoluteFillObject, backgroundColor: 'transparent', justifyContent: 'flex-end' },
  // Full-height sheet, so the list starts at the top of the screen instead
  // of hugging the bottom — same as the Withdraw / Inflow / Top-up pickers.
  modalContent: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 32,
  },
  modalHandle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.softGrey,
    marginBottom: 16,
  },
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  modalTitle: { fontFamily: 'Inter', fontSize: 16, fontWeight: '600', color: COLORS.primaryNavy },
  modalTitleAside: { fontFamily: 'Inter', fontSize: 12, color: COLORS.blueSteel },
  modalList: { flex: 1 },
  modalEmpty: { fontFamily: 'Inter', fontSize: 14, color: COLORS.blueSteel },
  // White tiles on the light sheet, like the Withdraw picker rows.
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 8,
    backgroundColor: COLORS.white,
  },
  modalOptionSelected: { borderColor: COLORS.brandOrange },
  modalOptionIcon: { width: 24, height: 24, marginRight: 12 },
  modalOptionTextContainer: { flex: 1 },
  modalOptionName: { fontFamily: 'Inter', fontSize: 16, fontWeight: '500', color: COLORS.primaryNavy },
  modalOptionSubtitle: { fontFamily: 'Inter', fontSize: 12, color: COLORS.blueSteel },
  modalOptionAmount: { fontFamily: 'Inter', fontSize: 14, color: COLORS.primaryNavy },
  /* Success */
  successOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  successCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  successClose: { position: 'absolute', top: 12, right: 12, padding: 8 },
  successCloseText: { fontSize: 16, color: COLORS.blueSteel },
  successIcon: { width: 48, height: 48, marginBottom: 12 },
  successTitle: { fontFamily: 'Inter', fontSize: 20, fontWeight: '600', color: COLORS.primaryNavy },
  successSubtitle: { fontFamily: 'Inter', fontSize: 14, color: COLORS.blueSteel, marginTop: 4 },
  successAmountRow: { flexDirection: 'row', alignItems: 'baseline', marginTop: 16 },
  successAmount: { fontFamily: 'Inter', fontSize: 28, fontWeight: '600', color: COLORS.primaryNavy },
  successCurrency: { fontFamily: 'Inter', fontSize: 16, fontWeight: '500', color: COLORS.blueSteel },
  successDetails: { alignSelf: 'stretch', marginTop: 20 },
  successDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  successDetailLabel: { fontFamily: 'Inter', fontSize: 14, color: COLORS.blueSteel },
  successDetailValue: { fontFamily: 'Inter', fontSize: 14, color: COLORS.primaryNavy },
  successButton: {
    alignSelf: 'stretch',
    marginTop: 20,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.primaryNavy,
  },
  successButtonText: { fontFamily: 'Inter', fontSize: 16, fontWeight: '600', color: COLORS.white },
  successSecondaryButton: {
    alignSelf: 'stretch',
    marginTop: 8,
    height: 48,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.primaryNavy,
  },
  successSecondaryText: { fontFamily: 'Inter', fontSize: 16, fontWeight: '500', color: COLORS.primaryNavy },
  // An icon, not the ↗ character: iOS draws that one as a blue emoji tile.
  successSecondaryIcon: { width: 16, height: 16, marginLeft: 6, tintColor: COLORS.primaryNavy },
});

export default ExchangeScreen;
